<template>
  <div>
    <v-btn stacked @click="showChat" class="top-nav-icon">
      <v-icon v-if="!unreadCount || unreadCount === 0">mdi-chat</v-icon>
      <v-badge
        v-else
        :content="unreadCount"
        color="red"
        overlap
        class="top-right-badge"
      >
        <v-icon>mdi-chat</v-icon>
      </v-badge>
    </v-btn>

    <!-- Chat Overlay -->
    <v-dialog
      fullscreen
      v-model="chatOpened"
      transition="dialog-top-transition"
      class="dialog-box background-fix"
      :class="{
        'dialog-with-shadow': chatOpened,
        'mobile-app': onMobileApp,
      }"
    >
      <div class="d-flex background-fix">
        <div class="d-flex align-center ma-4" style="gap: 4px">
          <div class="chat-sidebar-title">Chat</div>
          <InfoTooltip
            :firstParagraph="tooltipFirstSentence"
            :secondParagraph="tooltipSecondSentence"
            :hideBullets="true"
          />
        </div>

        <v-btn icon size="small" @click="closeChat" class="my-2 mr-3 close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- <v-skeleton-loader
        v-show="!chatLoaded"
        type="card@3"
        style="height: 100%"
      /> -->

      <div
        v-if="!chatLoaded"
        class="loader-container background-fix"
        :style="{
          borderBottomLeftRadius: onMobileApp ? '0' : '12px',
          borderBottomRightRadius: onMobileApp ? '0' : '12px',
        }"
      >
        <HamburgerLoader />
      </div>

      <iframe
        v-show="chatLoaded"
        :src="chatSrc"
        :style="{
          width: '100%',
          height: '100%',
          border: 'none',
          borderBottomLeftRadius: onMobileApp ? '0' : '12px',
          borderBottomRightRadius: onMobileApp ? '0' : '12px',
        }"
        class="chat-iframe"
      />
    </v-dialog>
  </div>
</template>
<script setup>
import { Auth } from 'aws-amplify';
import { useRootStore } from '../../stores/root';
import { storeToRefs } from 'pinia';
import { ref, watch, onMounted, onUnmounted } from 'vue';
import ChatWebSocketService from './ChatWebSocketService';
import HamburgerLoader from '../loading/HamburgerLoader.vue';
import InfoTooltip from '../InfoTooltip.vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const store = useRootStore();
const {
  userId,
  orgId,
  chatOpened,
  chatGroupId,
  chatTimestamp,
  chatSenderId,
  onMobileApp,
} = storeToRefs(store);

const tooltipFirstSentence = t('all-chats-are-monitored-by');
const tooltipSecondSentence = t('please-keep-messages-respectful');
const unreadCount = ref(null);
const origin = window.location.origin;
const chatInitialized = ref(false);
const chatLoaded = ref(false);
const chatSrc = ref('');
const TOUCH_LOCK_CLASS = 'chat-touch-lock';
const VIEWPORT_META_SELECTOR = 'meta[name="viewport"]';
const LOCKED_VIEWPORT_CONTENT =
  'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
const DEFAULT_VIEWPORT_CONTENT = 'width=device-width, initial-scale=1.0';
const shouldUseMobileZoomLock =
  typeof navigator !== 'undefined' &&
  /(Android|iPhone|iPad|iPod)/i.test(
    navigator.userAgent || navigator.vendor || '',
  );
/** @type {string | null} */
let originalViewportContent = null;
let zoomGuardsAttached = false;

const lockViewportZoom = () => {
  if (!shouldUseMobileZoomLock || typeof document === 'undefined') {
    return;
  }
  const viewport = document.querySelector(VIEWPORT_META_SELECTOR);
  if (!viewport) {
    return;
  }
  const currentContent =
    viewport.getAttribute('content') || DEFAULT_VIEWPORT_CONTENT;
  if (!originalViewportContent) {
    originalViewportContent = currentContent;
  }
  if (currentContent !== LOCKED_VIEWPORT_CONTENT) {
    viewport.setAttribute('content', LOCKED_VIEWPORT_CONTENT);
  }
};

const unlockViewportZoom = () => {
  if (
    !shouldUseMobileZoomLock ||
    typeof document === 'undefined' ||
    !originalViewportContent
  ) {
    return;
  }
  const viewport = document.querySelector(VIEWPORT_META_SELECTOR);
  if (viewport) {
    viewport.setAttribute('content', originalViewportContent);
  }
  originalViewportContent = null;
};

const applyTouchActionLock = () => {
  if (!shouldUseMobileZoomLock || typeof document === 'undefined') {
    return;
  }
  document.body.classList.add(TOUCH_LOCK_CLASS);
};

const removeTouchActionLock = () => {
  if (!shouldUseMobileZoomLock || typeof document === 'undefined') {
    return;
  }
  document.body.classList.remove(TOUCH_LOCK_CLASS);
};

const attachZoomGuards = () => {
  if (zoomGuardsAttached) {
    return;
  }
  // Prevent zoom via viewport meta tag and CSS
  applyTouchActionLock();
  lockViewportZoom();
  zoomGuardsAttached = true;
};

const detachZoomGuards = () => {
  if (!zoomGuardsAttached) {
    return;
  }
  // Restore zoom settings
  removeTouchActionLock();
  unlockViewportZoom();
  zoomGuardsAttached = false;
};

function closeChat() {
  chatOpened.value = false;
  chatLoaded.value = false;
}

async function handleMessage(event) {
  // Validate the origin for security
  if (event.origin !== origin) {
    console.warn('Received message from unauthorized origin:', event.origin);
    return;
  }

  const eventData = event.data;
  try {
    if (eventData === 'pageLoaded') {
      chatLoaded.value = true;
    } else if (eventData.type === 'downloadFile') {
      window.sendMessageToApp?.('downloadFile', {
        base64Data: eventData.base64Data,
        filename: eventData.fileName,
        fileType: eventData.fileType,
      });
    } else if (eventData.type === 'phone') {
      window.sendMessageToApp?.('openPhone', {
        phoneNumber: eventData.data,
      });
    } else if (eventData.type === 'email') {
      window.sendMessageToApp?.('openEmail', {
        email: eventData.data,
      });
    } else if (eventData.type === 'link') {
      window.sendMessageToApp?.('openLink', {
        url: eventData.data,
      });
    }
  } catch (error) {
    console.error('Error processing message:', error);
  }
}

async function initGlobalWebSocket() {
  // check if inside iframe
  if (window !== window.top) {
    // console.log('Skipping global WebSocket connection in iframe');
    return;
  }

  if (!userId.value || !orgId.value) {
    console.warn('User or orgId not available for WebSocket initialization');
    return;
  }

  // Initialize the WebSocket service
  ChatWebSocketService.init({
    baseUrl: import.meta.env.VITE_APP_CHAT_WEBSOCKET_URL,
    orgId: orgId.value,
    participantId: userId.value,
  });

  // Connect to global channel
  await ChatWebSocketService.connectGlobal().catch((error) => {
    console.error('Failed to establish global WebSocket connection:', error);
  });
  // setup global listener here
  ChatWebSocketService.onMessage('unread_stats_updated', (data) => {
    unreadCount.value = data.payload.orgUnreadCount;
  });
  getUnreadCount();
}

async function getToken() {
  const currentSession = await Auth.currentSession();
  const { idToken } = currentSession;
  return idToken.jwtToken;
}

async function getUnreadCount() {
  const token = await getToken();
  const payload = {
    participantId: userId.value,
    orgId: orgId.value,
  };
  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_APP_CHAT_API_BASE_URL}/getUnreadStatsCount`,
      payload,
      {
        headers: {
          'x-auth-token': token,
        },
      },
    );
    unreadCount.value = data.count;
  } catch (error) {
    console.error('Failed to get unread count:', error);
  }
}

function showChat() {
  const base = `${origin}/portal/chat`;
  const params = new URLSearchParams();
  if (chatGroupId.value) {
    params.append('groupId', chatGroupId.value);
  }
  if (chatTimestamp.value) {
    params.append('timestamp', chatTimestamp.value);
  }
  if (chatSenderId.value) {
    params.append('chatSenderId', chatSenderId.value);
  }
  if (onMobileApp.value) {
    params.append('isMobileApp', 'true');
  }
  chatSrc.value = params.toString() ? `${base}?${params.toString()}` : base;
  chatGroupId.value = null;
  chatInitialized.value = true;
  chatOpened.value = true;
}

onMounted(() => {
  window.addEventListener('message', handleMessage);
  initGlobalWebSocket();
});

watch(
  chatOpened,
  (value) => {
    if (value) {
      showChat();
      // Prevent zoom when chat opens
      attachZoomGuards();
    } else {
      // Restore zoom settings when chat closes
      detachZoomGuards();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
  ChatWebSocketService.disconnect();

  // Restore zoom settings if still attached
  detachZoomGuards();
});
</script>
<i18n>
{
  "en": {
    "all-chats-are-monitored-by": "All chats are monitored by admins.",
    "please-keep-messages-respectful": "Please keep messages respectful and work-appropriate."
  },
  "es": {
    "all-chats-are-monitored-by": "Todos los chats son monitoreados por administradores.",
    "please-keep-messages-respectful": "Por favor, mantenga los mensajes respetuosos y apropiados para el trabajo."
  }
}
</i18n>
<style scoped>
.header-title {
  font-size: 16px;
  font-weight: 500;
}

.close-btn {
  margin-left: auto;
}

.chat-sidebar-title {
  font-size: 18px;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface));
}

.background-fix {
  background: rgb(var(--v-theme-surface-page)) !important;
}

.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

@keyframes shadowFadeIn {
  0% {
    box-shadow: none;
  }
  100% {
    box-shadow:
      0px 5px 22px 4px rgba(0, 0, 0, 0.12),
      0px 12px 17px 2px rgba(0, 0, 0, 0.14),
      0px 7px 8px -4px rgba(0, 0, 0, 0.2);
  }
}

.dialog-with-shadow {
  animation: shadowFadeIn 0.1s ease-out 0.3s forwards;
  box-shadow: none;
}

:global(body.chat-touch-lock) {
  touch-action: pan-y;
  overscroll-behavior: contain;
}

/* media query for desktop */
@media (min-width: 1024px) {
  .dialog-box {
    width: 85vw !important;
    height: 80vh !important;
    max-width: 1200px !important;
    margin: 0 auto !important;
    transform: none !important;
    align-self: flex-start !important;
  }

  .dialog-box:not(.mobile-app) {
    border-radius: 0 0 8px 8px !important;
  }
}
</style>
